import { useState, useRef, useEffect, useCallback } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SlideIcon from "../assets/slide-icon.png";

gsap.registerPlugin(ScrollTrigger);

import p1before from "../assets/gallery/patient-1-before.jpeg";
import p1after  from "../assets/gallery/patient-1-after.jpeg";
import p2before from "../assets/gallery/patient-2-before.jpeg";
import p2after  from "../assets/gallery/patient-2-after.jpg";
import p3before from "../assets/gallery/patient-3-before.jpg";
import p3after  from "../assets/gallery/patient-3-after.jpg";
import p4before from "../assets/gallery/patient-4-before.jpg";
import p4after  from "../assets/gallery/patient-4-after.jpg";
import p5before from "../assets/gallery/patient-5-before.jpg";
import p5after  from "../assets/gallery/patient-5-after.jpg";
import p6before from "../assets/gallery/patient-6-before.jpg";
import p6after  from "../assets/gallery/patient-6-after.jpg";
import p7before from "../assets/gallery/patient-7-before.jpg";
import p7after  from "../assets/gallery/patient-7-after.jpg";
import p8before from "../assets/gallery/patient-8-before.jpg";
import p8after  from "../assets/gallery/patient-8-after.jpg";
import p9before from "../assets/gallery/patient-9-before.jpg";
import p9after  from "../assets/gallery/patient-9-after.jpg";

const patients = [
  { id: 1, grafts: "3500 Grafts", method: "Sapphire FUE Method", before: p1before, after: p1after },
  { id: 2, grafts: "4000 Grafts", method: "DHI Method",           before: p2before, after: p2after },
  { id: 3, grafts: "3200 Grafts", method: "Sapphire FUE Method", before: p3before, after: p3after },
  { id: 4, grafts: "4500 Grafts", method: "DHI Method",           before: p4before, after: p4after },
  { id: 5, grafts: "3800 Grafts", method: "Sapphire FUE Method", before: p5before, after: p5after },
  { id: 6, grafts: "3000 Grafts", method: "FUT Method",           before: p6before, after: p6after },
  { id: 7, grafts: "4200 Grafts", method: "DHI Method",           before: p7before, after: p7after },
  { id: 8, grafts: "3600 Grafts", method: "Sapphire FUE Method", before: p8before, after: p8after },
  { id: 9, grafts: "2800 Grafts", method: "FUE Method",           before: p9before, after: p9after },
];

// ─── DRAG REVEAL ─────────────────────────────────────────────────────────────
function DragReveal({ patient }) {
  const [position, setPosition] = useState(50);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);

  const getPercent = useCallback((clientX) => {
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    return (x / rect.width) * 100;
  }, []);

  const onMouseDown = (e) => { e.preventDefault(); setDragging(true); };
  const onMouseMove = useCallback((e) => {
    if (!dragging) return;
    setPosition(getPercent(e.clientX));
  }, [dragging, getPercent]);
  const onMouseUp = useCallback(() => setDragging(false), []);
  const onTouchStart = (e) => { e.stopPropagation(); setDragging(true); };
  const onTouchMove = useCallback((e) => {
    if (!dragging) return;
    setPosition(getPercent(e.touches[0].clientX));
  }, [dragging, getPercent]);
  const onTouchEnd = useCallback(() => setDragging(false), []);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [onMouseMove, onMouseUp, onTouchMove, onTouchEnd]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden rounded-xl select-none"
      style={{
        cursor: dragging ? "ew-resize" : "col-resize",
        boxShadow: "0 12px 48px rgba(100,70,20,0.18), 0 0 0 1px rgba(160,120,40,0.2)",
        touchAction: "pan-y",
      }}
    >
      <img src={patient.after} alt="After" draggable={false} className="absolute inset-0 w-full h-full object-contain object-top" />
      <img src={patient.before} alt="Before" draggable={false} className="absolute inset-0 w-full h-full object-contain object-top"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }} />

      <div className="absolute top-2 left-2 sm:top-4 sm:left-4 px-2.5 sm:px-3 py-1 rounded-full text-white/85 border border-white/15"
        style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.18em", background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)", opacity: position > 12 ? 1 : 0, transition: "opacity 0.2s" }}>
        BEFORE
      </div>

      <div className="absolute top-2 right-2 sm:top-4 sm:right-4 px-2.5 sm:px-3 py-1 rounded-full font-bold"
        style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.18em", background: "rgba(201,168,76,0.88)", color: "#1a0f00", opacity: position < 88 ? 1 : 0, transition: "opacity 0.2s" }}>
        AFTER
      </div>

      <div className="absolute top-0 bottom-0 w-0.5 pointer-events-none"
        style={{ left: `${position}%`, transform: "translateX(-50%)", background: "linear-gradient(to bottom, rgba(201,168,76,0.1), #c9a84c 30%, #c9a84c 70%, rgba(201,168,76,0.1))" }} />

      <div onMouseDown={onMouseDown} onTouchStart={onTouchStart}
        className="absolute top-1/2 flex items-center justify-center rounded-full bg-white z-10"
        style={{ left: `${position}%`, transform: "translate(-50%, -50%)", width: "40px", height: "40px", cursor: "ew-resize", border: "2px solid rgba(201,168,76,0.55)", boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>
        <img src={SlideIcon} alt="slide" />
      </div>

      <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full whitespace-nowrap"
        style={{ fontFamily: "'Raleway', sans-serif", fontSize: "9px", letterSpacing: "0.1em", color: "rgba(255,255,255,0.7)", background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)" }}>
        DRAG TO REVEAL
      </div>
    </div>
  );
}

// ─── THUMBNAIL CARD ───────────────────────────────────────────────────────────
function ThumbCard({ patient, isActive, onClick }) {
  return (
    <div onClick={onClick}
      className="flex items-center gap-2 sm:gap-2.5 p-2.5 sm:p-3 rounded-xl cursor-pointer flex-shrink-0 transition-all duration-200"
      style={{ border: isActive ? "1px solid rgba(160,120,40,0.55)" : "1px solid rgba(160,120,40,0.15)", background: isActive ? "rgba(201,168,76,0.1)" : "#ffffff", boxShadow: isActive ? "0 2px 16px rgba(160,120,40,0.1)" : "0 1px 4px rgba(0,0,0,0.06)" }}>

      <div className="relative rounded-md overflow-hidden flex-shrink-0" style={{ width: "56px", height: "56px" }}>
        <img src={patient.before} alt="before" className="w-full h-full object-cover object-top" />
        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-white/80"
          style={{ fontFamily: "'Cinzel', serif", fontSize: "6px", letterSpacing: "0.1em", background: "rgba(0,0,0,0.75)" }}>BEFORE</span>
      </div>

      <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center"
        style={{ background: "rgba(201,168,76,0.12)", border: "1px solid rgba(201,168,76,0.3)" }}>
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
          <path d="M5 12h14M13 6l6 6-6 6" stroke="#c9a84c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      <div className="relative rounded-md overflow-hidden flex-shrink-0" style={{ width: "56px", height: "56px" }}>
        <img src={patient.after} alt="after" className="w-full h-full object-cover object-top" />
        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded font-bold"
          style={{ fontFamily: "'Cinzel', serif", fontSize: "6px", letterSpacing: "0.1em", background: "rgba(201,168,76,0.85)", color: "#1a0f00" }}>AFTER</span>
      </div>

      <div className="flex flex-col gap-0.5 min-w-0">
        <p className="truncate" style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", fontWeight: 700, color: isActive ? "#a07828" : "#1a0f02", letterSpacing: "0.04em" }}>{patient.grafts}</p>
        <p className="truncate" style={{ fontFamily: "'Raleway', sans-serif", fontSize: "9px", color: "#6b5535", lineHeight: 1.4 }}>{patient.method}</p>
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function BeforeAfterGallery() {
  const [active, setActive] = useState(0);
  const listRef = useRef(null);
  const sectionRef = useRef(null);

  // scroll active thumb into view (works for both vertical desktop list and horizontal mobile strip)
  useEffect(() => {
    const container = listRef.current;
    const el = container?.children[active];

    if (!container || !el) return;

    const isRow = window.getComputedStyle(container).flexDirection === "row";

    if (isRow) {
      container.scrollTo({
        left: el.offsetLeft - container.offsetLeft - 8,
        behavior: "smooth",
      });
    } else {
      container.scrollTo({
        top: el.offsetTop - container.offsetTop,
        behavior: "smooth",
      });
    }
  }, [active]);

  // ── GSAP ScrollTrigger: dark → cream-white background ──────────────────────
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        sectionRef.current,
        { backgroundColor: "#1a0f02" },
        {
          backgroundColor: "#f5ede0",
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            end: "top 20%",
            scrub: 1,
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);
  // ──────────────────────────────────────────────────────────────────────────

  const current = patients[active];

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700;900&family=Raleway:wght@300;400;500&display=swap"
        rel="stylesheet"
      />

      <style>{`
        .bag-thumb-list::-webkit-scrollbar { height: 6px; width: 6px; }
        .bag-thumb-list::-webkit-scrollbar-thumb { background: rgba(160,120,40,0.35); border-radius: 999px; }
        .bag-thumb-list::-webkit-scrollbar-track { background: rgba(0,0,0,0.05); }
      `}</style>

      <section ref={sectionRef} className="w-full py-14 sm:py-20 lg:py-24 overflow-hidden" style={{ background: "#1a0f02" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">

          {/* HEADER */}
          <div className="text-center mb-8 sm:mb-12 lg:mb-14">
            <p className="mb-3 tracking-[0.3em] uppercase text-[9px] sm:text-[10px]"
              style={{ fontFamily: "'Cinzel', serif", color: "#c9a84c" }}>
              Real Patients. Real Results.
            </p>
            <h2 className="font-black uppercase mb-3"
              style={{ fontFamily: "'Cinzel', serif", fontSize: "clamp(1.4rem,5vw,2.4rem)", letterSpacing: "0.04em", lineHeight: 1.15, color: "#1a0f02" }}>
              Before &amp; After<br />Transformations
            </h2>
            <p className="mx-auto px-2" style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 400, fontSize: "14px", color: "#6b5535", maxWidth: "480px", lineHeight: 1.7 }}>
              Drag the slider to compare results from real patients across all four of our clinics.
            </p>
          </div>

          {/* BODY — stacks on mobile, two columns from lg up */}
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start">

            {/* ── RIGHT ON DESKTOP / TOP ON MOBILE: reveal panel — always visible, never hidden on mobile ── */}
           <div className="flex order-1 lg:order-2 flex-1 w-full flex-col mt-10">

  <div 
    className="w-full flex-shrink-0" 
    style={{ height: "clamp(300px, 85vw, 500px)" }}
  >
    <DragReveal patient={current} key={active} />
  </div>


              {/* Meta row */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-3 sm:mt-4 flex-shrink-0">
                <span className="px-3 sm:px-4 py-1 sm:py-1.5 rounded-full font-bold tracking-wide"
                  style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", color: "#a07828", border: "1px solid rgba(160,120,40,0.35)", background: "rgba(201,168,76,0.12)" }}>
                  {current.grafts}
                </span>
                <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: "12px", color: "#6b5535", fontWeight: 400 }}>
                  {current.method}
                </span>

                <div className="ml-auto flex items-center gap-2 sm:gap-3">
                  <span style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", color: "rgba(100,70,30,0.4)", letterSpacing: "0.1em" }}>
                    {String(active + 1).padStart(2, "0")} / {String(patients.length).padStart(2, "0")}
                  </span>
                  <button onClick={() => setActive(i => Math.max(0, i - 1))} disabled={active === 0}
                    className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all duration-200"
                    style={{ border: "1px solid rgba(160,120,40,0.25)", background: "#fff", color: "#6b5535", opacity: active === 0 ? 0.3 : 1, cursor: active === 0 ? "default" : "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
                    onMouseEnter={e => { if (active !== 0) { e.currentTarget.style.borderColor = "#a07828"; e.currentTarget.style.color = "#a07828"; }}}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(160,120,40,0.25)"; e.currentTarget.style.color = "#6b5535"; }}>
                    <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
                    </svg>
                  </button>
                  <button onClick={() => setActive(i => Math.min(patients.length - 1, i + 1))} disabled={active === patients.length - 1}
                    className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all duration-200"
                    style={{ border: "1px solid rgba(160,120,40,0.25)", background: "#fff", color: "#6b5535", opacity: active === patients.length - 1 ? 0.3 : 1, cursor: active === patients.length - 1 ? "default" : "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
                    onMouseEnter={e => { if (active !== patients.length - 1) { e.currentTarget.style.borderColor = "#a07828"; e.currentTarget.style.color = "#a07828"; }}}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(160,120,40,0.25)"; e.currentTarget.style.color = "#6b5535"; }}>
                    <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* ── LEFT ON DESKTOP / BOTTOM ON MOBILE: patient list ── */}
            <div className="order-2 lg:order-1 flex-shrink-0 w-full lg:w-[320px] flex flex-col">
              <p className="mb-2.5 sm:mb-3 tracking-[0.22em] uppercase"
                style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", color: "rgba(100,70,30,0.5)" }}>
                {patients.length} Patients — scroll to explore
              </p>

              {/* Horizontal scroll strip on mobile/tablet, vertical scroll list on desktop */}
              <div
                ref={listRef}
                className="bag-thumb-list flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-x-hidden lg:overflow-y-auto pb-2 lg:pb-0 pr-0 lg:pr-1"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgba(160,120,40,0.35) rgba(0,0,0,0.05)",
                }}
              >
                {patients.map((p, i) => (
                  <div key={p.id} className="w-[240px] sm:w-[260px] lg:w-auto flex-shrink-0">
                    <ThumbCard patient={p} isActive={i === active} onClick={() => setActive(i)} />
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}